// Imports
import { Injectable } from '@angular/core';
import { BundleCall } from '@ispm-client/modules/aggregator/services';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { DiagnosticOrderActionsService } from '@ispm/dashboard/test/services/diagnostic-order-actions.service';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IQuestionareData, IQuestionnaireResponseHasDependent } from '../interfaces/questionnaire-response.interface';
import { HttpInterceptorService } from '@ispm/core/services/http-interceptor.service';
import { IResourceReference } from '@ispm/core/interfaces/resource-reference.interface';
import { FormReflect } from '@ispm/shared/dynamic-form/classes/form-reflect.class';
import { isEmpty } from 'lodash';

/**
 *  parameters added for validating and saving questionnaire response
 */
const updateParameters = {
    partialUpdate: true,
    noFlatten: true,
    onlyUnique: true
};

const hiddenClassCss = 'hidden-form-item';
const getQuestionUrl: string = 'additionalConfiguration/checklist/getQuestions';

/**
 * Service responsible for questionnaire response and request
 * and handling questionnaire manipulations
 */
@Injectable()
export class QuestionnairesService {

    private organizationId: string = '';
    private applicationId: string = '';
    private questionnaireResponseCache$: Observable<any>;
    private orderId: string = '';
    private urlToPostData: string = '';
    private getQuestionnaireUrl: string = '';
    public isChecklistFormUpdated$ = new BehaviorSubject<boolean>(false);

    constructor(
        private _bundleCall: BundleCall,
        private genomicsStore: Store<IgenomicsState>,
        private diagnosticOrderActionsService: DiagnosticOrderActionsService,
        private _ispmHttpClient: HttpInterceptorService
    ) {
        this.genomicsStore.select('users').take(1).subscribe(data => {
            if (data && data.currentUser) {
                this.organizationId = data.currentUser.organization;
            }
        });
        this.setOrderIdAndPrepareUrls();
    }

    /**
     * sets current order id
     */
    private setOrderIdAndPrepareUrls() {
        this.diagnosticOrderActionsService.getState()
            .filter(state => !!state.order)
            .map(state => state.order)
            .subscribe(order => {
                this.orderId = order.id;
                this.urlToPostData = `diagnosticOrder/${this.orderId}`;
            });
    }


    /**
     * Returns questionnaire data
     */
    public getQuestionnaireData(): Observable<any> {
        return this._ispmHttpClient.get(this.urlToPostData).pipe(map(res => res.body));
    }

    /**
     * Returns questionnaire info
     */
    public getQuestionnairesInfo() {
        return Observable.combineLatest([this.getQuestionnaireData(), this.getQuestionnaireResponse()]);
    }

    /**
     * Returns all Questionnaire configuration
     * @returns Observable<IQuestionnaireResponse[]>
     */
    private getQuestionnaireResponse() {
        if (!this.questionnaireResponseCache$) {
            const application = JSON.parse(sessionStorage.getItem('currentApplication'));
            this.applicationId = application ? application.id : '';
            this.getQuestionnaireUrl = `${getQuestionUrl}?org_id=${this.organizationId}&app_id=${this.applicationId}`;
            this.questionnaireResponseCache$ = this._ispmHttpClient.get(this.getQuestionnaireUrl).pipe(map(res => res.body));
        }
        return this.questionnaireResponseCache$;
    }

    /**
     * Post questionnaire data to backend
     * @param payload holds payload to post
     * @param isChecklistValid holds boolean value for checklist validity
     */
    public postQuestionnaireData(payload: IQuestionareData, isChecklistValid: boolean): Observable<any> {
        return this._bundleCall.updateCall(this.prepareUpdateQuery(payload, isChecklistValid));
    }

    /**
     * Prepares and return update query
     * @param payload holds payload,
     * @param isChecklistValid holds boolean value for checklist validity
     */
    private prepareUpdateQuery(payload: IQuestionareData, isChecklistValid: boolean): any {
        let addValidBooleanToPayload = { ...payload };
        addValidBooleanToPayload.questionnaire.isChecklistValid = isChecklistValid;
        const currentPatient = this._bundleCall.getPatientContextForInterpolation().currentPatient;
        let data = {
            id: this.orderId,
            ...addValidBooleanToPayload,
            context: this.getPatientContext(currentPatient.id),
            resourceType: 'DiagnosticOrder'
        };
        return {
            queries: [{
                ...updateParameters,
                targetResource: 'DiagnosticOrder',
                formId: 'CHECKLIST',
                data: data
            }]
        };
    }

    /**
     * 
     * @param id holds id of the current patient
     * @returns {IResourceReference[]}
     */
    private getPatientContext(id): IResourceReference[] {
        return [{
            reference: id,
            resourceType: 'Patient'
        }];
    }

    /**
     * set Dependent Questionnaires reflects
     * @param dependantList holds list of dependent parameters
     * @returns {FormReflect[]}
     */
    public setDependentQuestionnairesReflects(dependantList: IQuestionnaireResponseHasDependent[]): FormReflect[] {
        if (dependantList && !isEmpty(dependantList)) {
            let hideQuestionnaireDependentList: FormReflect[] = [];
            let showQuestionnairesReflects: FormReflect[] = [];
            dependantList.map((item: IQuestionnaireResponseHasDependent) => {
                let otherValueSet: string = [...item.otherValueSets].join('|');
                otherValueSet = `${otherValueSet}|null|undefined`;
                item.dependents.map((dependentsName: string) => {
                    showQuestionnairesReflects = [...showQuestionnairesReflects, this.prepareReflects(dependentsName, item.value)];
                    hideQuestionnaireDependentList = [...hideQuestionnaireDependentList, this.prepareReflects(dependentsName, otherValueSet, hiddenClassCss, true)];
                });
            });
            return [...hideQuestionnaireDependentList, ... showQuestionnairesReflects];
        }
        else return [];
    }

    /**
     * Prepares individual reflects
     * @param affectedField holds name of the affecting field
     * @param applyRegex holds regex to be checked for
     * @param styleCss holds holds css class to set
     * @param setValue holds holds boolean to set fields value
     * @return {FormReflect}
     */
    private prepareReflects(affectedField: string, applyRegex: string, styleCss: string = '', setValue: boolean = false): FormReflect {
        let reflect: FormReflect = FormReflect.builder(affectedField).setStyleClass(styleCss)
                .setApplyRegex(`^${applyRegex}$`).setCheckOnInit(true);
        if(setValue) {
            reflect.setValue(null);
        }
        return reflect;
    }
}